import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { useDrawer } from '@/app/providers';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import type { ClosureRead, MeasuredQuantity } from '@/versions/v1/schema/metal-accounting';
import {
  closureRead,
  formatGapPct,
  formatMeasuredQuantity,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';

function isFigureQuantity(quantity: MeasuredQuantity): boolean {
  if (quantity.value === null) return false;
  return quantity.unit !== 'unmeasured';
}

function displayMeasuredQuantity(
  quantity: MeasuredQuantity,
  view: PeriodView,
): string {
  if (quantity.value === null || quantity.unit === 'unmeasured') return '—';
  return formatMeasuredQuantity(quantity, view.period);
}

function signOffChipLabel(signOff: ClosureRead['signOff']): string {
  if (signOff.state === 'signed') {
    return `Signed · ${signOff.by ?? 'Competent Person'} · ${signOff.at ?? '—'}`;
  }
  if (signOff.state === 'pending') {
    return 'Awaiting · Competent Person';
  }
  return 'None';
}

export function MetalAccountingBalanceTab({
  nodeId,
  view,
}: {
  nodeId: string;
  view: PeriodView;
}) {
  const read = closureRead(nodeId, view);
  const { open: openDrawer } = useDrawer();
  const unmeasured =
    read.tone === 'unmeasured' &&
    read.closurePct === null &&
    !isFigureQuantity(read.in) &&
    !isFigureQuantity(read.out) &&
    !isFigureQuantity(read.inventoryChange);

  const openCaseDrawer = (caseId: string, title: string) => {
    openDrawer('case-drawer', {
      kicker: 'Case',
      title,
      wide: true,
      data: { caseId },
    });
  };

  if (unmeasured && read.measurements.length === 0) {
    return <p className="text-sm text-muted">—</p>;
  }

  const kpiCells = [
    { label: 'In', display: displayMeasuredQuantity(read.in, view), figure: isFigureQuantity(read.in) },
    { label: 'Out', display: displayMeasuredQuantity(read.out, view), figure: isFigureQuantity(read.out) },
    {
      label: 'Δ inventory',
      display: displayMeasuredQuantity(read.inventoryChange, view),
      figure: isFigureQuantity(read.inventoryChange),
    },
    {
      label: 'Gap',
      display: read.closurePct !== null ? formatGapPct(read.closurePct) : '—',
      figure: read.closurePct !== null,
      tone: read.tone,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpiCells.map((cell) => (
          <div
            key={cell.label}
            className="rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-2"
          >
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted">{cell.label}</p>
            <p
              className={
                cell.figure
                  ? 'mt-1 text-lg font-semibold tabular tracking-tight text-zinc-900 dark:text-zinc-100'
                  : 'mt-1 text-sm text-muted'
              }
              style={
                'tone' in cell && cell.tone && cell.tone !== 'unmeasured' && cell.figure
                  ? {
                      color: vizToneColor(
                        cell.tone === 'warn' ? 'warn' : cell.tone === 'bad' ? 'bad' : 'ok',
                      ),
                    }
                  : undefined
              }
            >
              {cell.display}
            </p>
          </div>
        ))}
      </div>

      {read.measurements.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>Measurement</TH>
              <TH>Value</TH>
              <TH>Unit</TH>
              <TH>Source</TH>
            </TR>
          </THead>
          <TBody>
            {read.measurements.map((row) => (
              <TR key={row.id}>
                <TD className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  <span title={row.description}>
                    {row.label}
                    {row.measurement ? ` · ${row.measurement}` : ''}
                  </span>
                  {row.grossErrorFlag ? (
                    <button
                      type="button"
                      onClick={() =>
                        openCaseDrawer(row.grossErrorFlag!.caseId, row.grossErrorFlag!.drawerTitle)
                      }
                      className="ml-2 rounded-full border border-[var(--state-warning)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--state-warning)]"
                    >
                      {row.grossErrorFlag.chipLabel}
                    </button>
                  ) : null}
                </TD>
                <TD className="tabular-nums text-sm" title={row.description}>
                  {row.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </TD>
                <TD className="text-sm text-muted">{row.unit}</TD>
                <TD className="text-sm text-muted">{row.source}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}

      <div className="space-y-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted">Sign-off</p>
        <Pill tone={read.signOff.state === 'pending' ? 'warning' : 'default'}>
          {signOffChipLabel(read.signOff)}
        </Pill>
      </div>
    </div>
  );
}
